import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Facebook,
  Mail,
  Calendar,
  User,
  Target,
  MapPin,
  Users,
  FileText,
  Check,
  X,
  MessageSquare,
  Printer,
  Download,
  FileUp,
  ExternalLink,
  Copy
} from 'lucide-react';
import { buildCampaignPrefill } from '../../utils/campaignPrefill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  changes_requested: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const PLATFORM_ICONS = {
  facebook: Facebook,
  email: Mail
};

function CampaignView() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingDoc, setCreatingDoc] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const printRef = useRef(null);

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/duplicate`);
      const newCampaign = response.data.campaign;
      toast.success('Campaign duplicated');
      navigate('/campaigns/create', {
        state: buildCampaignPrefill(newCampaign, {
          notice: 'Loaded a copy — tweak and submit when ready.'
        })
      });
    } catch (error) {
      console.error('Duplicate campaign error:', error);
      toast.error('Failed to duplicate campaign');
    } finally {
      setDuplicating(false);
    }
  };

  useEffect(() => {
    const loadCampaign = async () => {
      try {
        // Use the dedicated single campaign endpoint
        const response = await axios.get(`${API}/campaigns/${campaignId}`);
        setCampaign(response.data.campaign || null);
      } catch (error) {
        console.error('Load campaign error:', error);
        if (error.response?.status === 404) {
          toast.error('Campaign not found');
        } else if (error.response?.status === 403) {
          toast.error('Access denied');
        } else {
          toast.error('Failed to load campaign');
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadCampaign();
  }, [campaignId]);

  const reloadCampaign = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}`);
      setCampaign(response.data.campaign || null);
    } catch (error) {
      console.error('Reload campaign error:', error);
      toast.error('Failed to reload campaign');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status, notes = '') => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/status`, { status, notes });
      toast.success(`Campaign ${status.replace('_', ' ')}`);
      reloadCampaign();
    } catch (error) {
      console.error('Update status error:', error);
      toast.error('Failed to update status');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const createGoogleDoc = async () => {
    setCreatingDoc(true);
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/create-doc`);
      
      if (response.data.document_url) {
        toast.success('Google Doc created!');
        window.open(response.data.document_url, '_blank');
      }
    } catch (error) {
      console.error('Create doc error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create Google Doc');
    } finally {
      setCreatingDoc(false);
    }
  };

  const downloadPDF = () => {
    // Create a styled printable version
    const printContent = printRef.current;
    if (!printContent) return;
    
    // Create a new window with custom styling for PDF
    const printWindow = window.open('', '_blank');
    const ads = campaign.ads || [];
    const avatar = campaign.avatar || {};
    const insights = campaign.insights || {};
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${campaign.clientName} - ${campaign.campaignName} Brief</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            color: #1a1a1a;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #00d4aa;
          }
          .header h1 {
            font-size: 28px;
            margin-bottom: 8px;
            color: #1a1a1a;
          }
          .header .client {
            font-size: 18px;
            color: #666;
          }
          .meta-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
            margin-bottom: 30px;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 8px;
          }
          .meta-item {
            text-align: center;
          }
          .meta-item .label {
            font-size: 11px;
            text-transform: uppercase;
            color: #666;
            letter-spacing: 1px;
            margin-bottom: 4px;
          }
          .meta-item .value {
            font-size: 16px;
            font-weight: 600;
            color: #1a1a1a;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #00d4aa;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e0e0e0;
          }
          .offer-box {
            background: linear-gradient(135deg, #f0fdf9 0%, #f8f9fa 100%);
            border: 2px solid #00d4aa;
            border-radius: 12px;
            padding: 24px;
            margin-bottom: 30px;
          }
          .offer-box h3 {
            font-size: 20px;
            margin-bottom: 12px;
          }
          .offer-box .cta {
            display: inline-block;
            background: #00d4aa;
            color: white;
            padding: 8px 16px;
            border-radius: 6px;
            font-weight: 600;
            margin-top: 12px;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            margin-bottom: 20px;
          }
          .info-item {
            padding: 12px;
            background: #f8f9fa;
            border-radius: 6px;
          }
          .info-item .label {
            font-size: 11px;
            text-transform: uppercase;
            color: #666;
            margin-bottom: 4px;
          }
          .info-item .value {
            font-size: 14px;
            color: #1a1a1a;
          }
          .text-block {
            background: #f8f9fa;
            padding: 16px;
            border-radius: 6px;
            margin-bottom: 12px;
          }
          .text-block .label {
            font-size: 11px;
            text-transform: uppercase;
            color: #666;
            margin-bottom: 8px;
          }
          .text-block .value {
            white-space: pre-wrap;
          }
          .ad-card {
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 16px;
            page-break-inside: avoid;
          }
          .ad-card h4 {
            font-size: 16px;
            margin-bottom: 4px;
            color: #00d4aa;
          }
          .ad-card .style {
            font-size: 12px;
            color: #666;
            margin-bottom: 12px;
          }
          .ad-card .headline {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 12px;
            color: #1a1a1a;
          }
          .ad-card .text {
            font-size: 14px;
            color: #333;
            white-space: pre-wrap;
            line-height: 1.7;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e0e0e0;
            text-align: center;
            color: #999;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .ad-card { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${campaign.campaignName}</h1>
          <div class="client">Client: ${campaign.clientName}</div>
        </div>
        
        <div class="meta-grid">
          <div class="meta-item">
            <div class="label">Platform</div>
            <div class="value">${(campaign.platform || 'N/A').toUpperCase()}</div>
          </div>
          <div class="meta-item">
            <div class="label">Ads</div>
            <div class="value">${ads.length}</div>
          </div>
          <div class="meta-item">
            <div class="label">Status</div>
            <div class="value">${(campaign.status || 'draft').toUpperCase()}</div>
          </div>
        </div>
        
        <div class="offer-box">
          <div class="section-title">Offer</div>
          <h3>${campaign.offer || 'Not specified'}</h3>
          <div class="cta">${campaign.cta || 'Learn More'}</div>
        </div>
        
        <div class="section">
          <div class="section-title">Target Audience</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="label">Age Range</div>
              <div class="value">${campaign.ageRange || 'Not specified'}</div>
            </div>
            <div class="info-item">
              <div class="label">Location</div>
              <div class="value">${campaign.location || 'Not specified'}</div>
            </div>
          </div>
          <div class="text-block">
            <div class="label">Who Is This Person</div>
            <div class="value">${campaign.whoIsThisPerson || avatar.whoIsThisPerson || 'Not specified'}</div>
          </div>
          <div class="text-block">
            <div class="label">Main Problem</div>
            <div class="value">${campaign.mainProblem || avatar.mainProblem || 'Not specified'}</div>
          </div>
        </div>
        
        ${insights.beforeState || insights.afterState ? `
        <div class="section">
          <div class="section-title">Customer Journey</div>
          <div class="info-grid">
            <div class="text-block">
              <div class="label">Before State</div>
              <div class="value">${insights.beforeState || 'Not specified'}</div>
            </div>
            <div class="text-block">
              <div class="label">After State</div>
              <div class="value">${insights.afterState || 'Not specified'}</div>
            </div>
          </div>
        </div>
        ` : ''}
        
        <div class="section">
          <div class="section-title">Ad Creatives (${ads.length})</div>
          ${ads.map((ad, i) => `
            <div class="ad-card">
              <h4>Ad ${i + 1}: ${ad.adName || `Ad ${i + 1}`}</h4>
              <div class="style">Style: ${ad.primaryTextStyle || 'Not specified'}</div>
              <div class="headline">${ad.headline || 'No headline'}</div>
              <div class="text">${ad.primaryText || 'No primary text'}</div>
            </div>
          `).join('')}
        </div>
        
        <div class="footer">
          <p>Generated by Epsillon Media Work Hub</p>
          <p>${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-white mb-4">Campaign not found</h2>
        <Button onClick={() => navigate('/campaigns')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Campaigns
        </Button>
      </div>
    );
  }

  const PlatformIcon = PLATFORM_ICONS[campaign.platform] || FileText;

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" ref={printRef}>
      {/* Header - Hide on print */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleDuplicate}
            disabled={duplicating}
            className="border-white/20 text-white hover:bg-white/10"
          >
            {duplicating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Copy className="w-4 h-4 mr-2" />
            )}
            Duplicate
          </Button>
          <Button
            variant="outline"
            onClick={downloadPDF}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
          <Button 
            variant="outline" 
            onClick={createGoogleDoc}
            disabled={creatingDoc}
            className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
          >
            {creatingDoc ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileUp className="w-4 h-4 mr-2" />
            )}
            Create Google Doc
          </Button>
          {campaign.status === 'pending' && (
            <>
              <Button
                onClick={() => updateStatus('approved')}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Check className="w-4 h-4 mr-1" />
                Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const notes = prompt('Enter feedback for changes:');
                  if (notes) updateStatus('changes_requested', notes);
                }}
                className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => updateStatus('rejected')}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <X className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Print Header - Show only on print */}
      <div className="hidden print:block text-center mb-8 border-b border-gray-300 pb-6">
        <h1 className="text-2xl font-bold text-gray-900">EPSILLON MEDIA</h1>
        <p className="text-sm text-gray-600">Campaign Brief</p>
        <p className="text-lg font-semibold mt-2">{campaign.clientName} | {campaign.campaignName}</p>
      </div>

      {/* Campaign Header Card */}
      <Card className="card mb-6 print:bg-white print:text-black print:border-gray-300">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#00d4aa]/20 rounded-lg print:bg-gray-100">
              <PlatformIcon className="w-8 h-8 text-[#00d4aa] print:text-gray-700" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-white print:text-black">
                  {campaign.campaignName}
                </h1>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[campaign.status]} print:bg-gray-200 print:text-gray-700 print:border-gray-300`}>
                  {campaign.status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-white/60 print:text-gray-600">
                <span className="flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  {campaign.clientName}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {campaign.submittedBy || 'Unknown'}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {campaign.launchDate || 'Not set'}
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {campaign.ads?.length || 0} ads
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Overview */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Offer & CTA */}
        <Card className="card print:bg-white print:text-black print:border-gray-300">
          <CardHeader>
            <CardTitle className="text-white print:text-black">Offer & Call to Action</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">OFFER / HOOK</p>
              <p className="text-white print:text-black">{campaign.offer || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">CALL TO ACTION</p>
              <p className="text-white print:text-black">{campaign.cta || 'Not specified'}</p>
            </div>
            {campaign.landingPageUrl && (
              <div>
                <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">LANDING PAGE</p>
                <a href={campaign.landingPageUrl} target="_blank" rel="noopener noreferrer" 
                   className="text-[#00d4aa] hover:underline print:text-blue-600">
                  {campaign.landingPageUrl}
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Target Audience */}
        <Card className="card print:bg-white print:text-black print:border-gray-300">
          <CardHeader>
            <CardTitle className="text-white print:text-black">Target Audience</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">AGE RANGE</p>
                <p className="text-white print:text-black">{campaign.ageRange || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">LOCATION</p>
                <p className="text-white print:text-black flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {campaign.location || 'Not specified'}
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">WHO IS THIS PERSON?</p>
              <p className="text-white/80 print:text-gray-700 text-sm">{campaign.whoIsThisPerson || 'Not specified'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">MAIN PROBLEM</p>
                <p className="text-white/80 print:text-gray-700 text-sm">{campaign.mainProblem || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">WHAT THEY WANT</p>
                <p className="text-white/80 print:text-gray-700 text-sm">{campaign.whatTheyWant || 'Not specified'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Naming Conventions */}
      {campaign.naming && (
        <Card className="card mb-6 print:bg-white print:text-black print:border-gray-300">
          <CardHeader>
            <CardTitle className="text-white print:text-black">Naming Conventions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 bg-white/5 rounded-lg print:bg-gray-100">
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">CAMPAIGN NAME</p>
              <p className="text-sm text-white font-mono break-all print:text-black">{campaign.naming.campaign}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg print:bg-gray-100">
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">AD SET (COLD)</p>
              <p className="text-sm text-white font-mono break-all print:text-black">{campaign.naming.adsetCold}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg print:bg-gray-100">
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">AD SET (RETARGETING)</p>
              <p className="text-sm text-white font-mono break-all print:text-black">{campaign.naming.adsetRetarget}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deep Insights */}
      <Card className="card mb-6 print:bg-white print:text-black print:border-gray-300">
        <CardHeader>
          <CardTitle className="text-white print:text-black">Deep Customer Insights</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Before/After */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20 print:bg-red-50 print:border-red-200">
              <p className="text-xs font-mono text-red-400 print:text-red-600 mb-2">BEFORE STATE</p>
              <p className="text-white/80 print:text-gray-700 text-sm">{campaign.beforeState || 'Not specified'}</p>
            </div>
            <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20 print:bg-green-50 print:border-green-200">
              <p className="text-xs font-mono text-green-400 print:text-green-600 mb-2">AFTER STATE</p>
              <p className="text-white/80 print:text-gray-700 text-sm">{campaign.afterState || 'Not specified'}</p>
            </div>
          </div>

          {/* Key Insights */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">COMPELLING FACT</p>
              <p className="text-white/80 print:text-gray-700 text-sm">{campaign.compellingFact || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">BIG PROMISE</p>
              <p className="text-white/80 print:text-gray-700 text-sm">{campaign.bigPromise || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">EMOTIONAL PAYOFF</p>
              <p className="text-white/80 print:text-gray-700 text-sm">{campaign.emotionalPayoff || 'Not specified'}</p>
            </div>
          </div>

          {/* Objections */}
          {campaign.objections && (
            <div>
              <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-2">OBJECTIONS TO OVERCOME</p>
              <p className="text-white/80 print:text-gray-700 text-sm">{campaign.objections}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ads */}
      {campaign.ads && campaign.ads.length > 0 && (
        <Card className="card mb-6 print:bg-white print:text-black print:border-gray-300">
          <CardHeader>
            <CardTitle className="text-white print:text-black">Ad Creative ({campaign.ads.length} Ads)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {campaign.ads.map((ad, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg border border-white/10 print:bg-gray-50 print:border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-mono text-[#00d4aa] print:text-gray-700">{ad.adName}</span>
                  <span className="text-xs text-white/40 print:text-gray-500">{ad.primaryTextStyle} Style</span>
                </div>
                <div className="mb-3">
                  <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">HEADLINE</p>
                  <p className="text-lg font-semibold text-white print:text-black">{ad.headline}</p>
                </div>
                <div>
                  <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">PRIMARY TEXT</p>
                  <p className="text-white/80 print:text-gray-700 text-sm whitespace-pre-wrap">{ad.primaryText}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Budget & Schedule */}
      {(campaign.dailyBudget || campaign.startDate) && (
        <Card className="card mb-6 print:bg-white print:text-black print:border-gray-300">
          <CardHeader>
            <CardTitle className="text-white print:text-black">Budget & Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {campaign.dailyBudget && (
                <div>
                  <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">DAILY BUDGET</p>
                  <p className="text-white print:text-black">${campaign.dailyBudget}</p>
                </div>
              )}
              {campaign.campaignDuration && (
                <div>
                  <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">DURATION</p>
                  <p className="text-white print:text-black">{campaign.campaignDuration} days</p>
                </div>
              )}
              {campaign.startDate && (
                <div>
                  <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">START DATE</p>
                  <p className="text-white print:text-black">{campaign.startDate}</p>
                </div>
              )}
              {campaign.endDate && (
                <div>
                  <p className="text-xs font-mono text-white/40 print:text-gray-500 mb-1">END DATE</p>
                  <p className="text-white print:text-black">{campaign.endDate}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Footer - Print only */}
      <div className="hidden print:block text-center mt-8 pt-4 border-t border-gray-300">
        <p className="text-sm text-gray-500">
          Prepared by Epsillon Media | {new Date().toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

export default CampaignView;
